/**
 * useGooglePlacesScript
 *
 * Singleton-safe hook that loads the Google Maps JavaScript API script once,
 * regardless of how many AddressAutocomplete components are mounted.
 *
 * Rules:
 * - Only one <script> tag is ever injected, even if multiple components mount simultaneously.
 * - All callers share the same load promise.
 * - If the API key is missing, isReady stays false and loadError is set — components fall back to plain <Input>.
 */

import { useState, useEffect } from 'react';

type LoadState = 'idle' | 'loading' | 'ready' | 'error';

// Module-level singleton — survives component remounts and route changes
let loadState: LoadState = 'idle';
let loadPromise: Promise<void> | null = null;
const listeners = new Set<(state: LoadState) => void>();

function notifyListeners(state: LoadState) {
  loadState = state;
  listeners.forEach((fn) => fn(state));
}

function loadScript(apiKey: string): Promise<void> {
  if (loadPromise) return loadPromise;

  // Check if the script tag already exists (e.g., injected by something else)
  const existing = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
  if (existing) {
    // Script already in DOM — wait for google.maps to become available
    loadPromise = new Promise((resolve, reject) => {
      const poll = setInterval(() => {
        if (window.google?.maps) {
          clearInterval(poll);
          notifyListeners('ready');
          resolve();
        }
      }, 50);
      // Timeout after 10s
      setTimeout(() => {
        clearInterval(poll);
        if (!window.google?.maps) {
          notifyListeners('error');
          reject(new Error('Google Maps API load timeout'));
        }
      }, 10000);
    });
    return loadPromise;
  }

  notifyListeners('loading');

  loadPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    // Use the modern importLibrary bootstrap pattern recommended by Google
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async&v=weekly`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      // Poll until google.maps.places is available (async loading may not be immediate)
      const poll = setInterval(() => {
        if (window.google?.maps?.places) {
          clearInterval(poll);
          notifyListeners('ready');
          resolve();
        }
      }, 50);
      setTimeout(() => {
        clearInterval(poll);
        if (!window.google?.maps?.places) {
          notifyListeners('error');
          reject(new Error('google.maps.places not available after script load'));
        }
      }, 10000);
    };

    script.onerror = () => {
      notifyListeners('error');
      reject(new Error('Google Maps script failed to load'));
    };

    document.head.appendChild(script);
  });

  return loadPromise;
}

interface UsePlacesScriptResult {
  isReady: boolean;
  loadError: boolean;
}

export function useGooglePlacesScript(): UsePlacesScriptResult {
  const [state, setState] = useState<LoadState>(loadState);

  useEffect(() => {
    const apiKey = import.meta.env.VITE_GOOGLE_PLACES_API_KEY as string | undefined;

    if (!apiKey) {
      // No key configured — stay in idle (plain input fallback)
      return;
    }

    // If already loaded, nothing to do
    if (loadState === 'ready') {
      setState('ready');
      return;
    }

    if (loadState === 'error') {
      setState('error');
      return;
    }

    // Register listener to receive state changes from the singleton
    const listener = (newState: LoadState) => setState(newState);
    listeners.add(listener);

    // Trigger load (idempotent — will return existing promise if already loading)
    loadScript(apiKey).catch(() => {
      // Error is already broadcast via notifyListeners
    });

    return () => {
      listeners.delete(listener);
    };
  }, []);

  return {
    isReady: state === 'ready',
    loadError: state === 'error',
  };
}

// Extend Window type for TypeScript
declare global {
  interface Window {
    google: typeof google;
  }
}
